import type {
  ApiRoutersPortfoliosAssetsImagesImagesImageViewFields as ImageViewFields,
  SearchImagesQueryVariables,
} from '@vizit/api';
import type { ImageType, SortDirection } from '@vizit/api';

/**
 * Enum representing the available action types for the asset table filter reducer
 */
enum AssetTableActionType {
  SET_PRODUCT_CATEGORY_ID = 'SET_PRODUCT_CATEGORY_ID',
  SET_IMAGE_TYPE = 'SET_IMAGE_TYPE',
  SET_LIMIT = 'SET_LIMIT',
  SET_SORT_BY = 'SET_SORT_BY',
  SET_SORT_DIRECTION = 'SET_SORT_DIRECTION',
}

interface AssetTableState {
  /**
   * Number of items to fetch per page
   *
   * @optional
   * @type {number}
   * @default 12
   */
  limit?: number;
  /**
   * Sort field for the assets
   *
   * @optional
   * @type {ImageViewFields}
   * @default 'created_at'
   */
  sortBy?: ImageViewFields;
  /**
   * Sort direction for the assets
   *
   * @optional
   * @type {'asc' | 'desc'}
   * @default 'desc'
   */
  sortDirection?: SortDirection;
  /**
   * The product category ID to filter by
   * @optional
   * @type {string}
   */
  productCategoryId?: string | null;
  /**
   * The product category name to filter by
   * @optional
   * @type {string}
   */
  productCategoryName?: string;
  /**
   * The image type to filter by (hero or carousel)
   * @optional
   * @type {ImageType}
   */
  imageType?: ImageType | null;
}

/**
 * Default initial state for the asset table filters
 */
const DEFAULT_FILTER_STATE: AssetTableState = {
  productCategoryId: null,
  productCategoryName: '',
  imageType: null,
  limit: 12,
  sortBy: 'created_at',
  sortDirection: 'desc',
};

/**
 * Transforms the asset table state into query parameters for the API
 * @param state - The current asset table state
 * @returns Query parameters object for the search images API
 */
function transformStateToQueryParams(
  state: AssetTableState,
): Omit<
  SearchImagesQueryVariables,
  'workspace_id' | 'offset' | 'source' | 'exclude_source' | 'parent_id' | 'image_ids'
> {
  const { limit, sortBy, sortDirection, productCategoryId, imageType } = state;

  return {
    limit: limit ?? DEFAULT_FILTER_STATE.limit,
    sort_by: sortBy ?? DEFAULT_FILTER_STATE.sortBy,
    sort_direction: sortDirection ?? DEFAULT_FILTER_STATE.sortDirection,
    product_category_id:
      !productCategoryId || productCategoryId === DEFAULT_FILTER_STATE.productCategoryId
        ? undefined
        : [productCategoryId],
    image_type: !imageType || imageType === DEFAULT_FILTER_STATE.imageType ? undefined : imageType,
  };
}

/**
 * Reducer function for managing asset table filter state
 * @param currentState - The current state
 * @param action - The action to apply to the state
 * @returns The new state after applying the action
 */
function assetTableFilterReducer(
  currentState: AssetTableState,
  action: { type: AssetTableActionType; payload: Partial<AssetTableState> },
) {
  const { limit, sortBy, sortDirection, productCategoryId, productCategoryName, imageType } = action.payload ?? {};

  switch (action.type) {
    case AssetTableActionType.SET_PRODUCT_CATEGORY_ID:
      return { ...currentState, productCategoryId, productCategoryName };
    case AssetTableActionType.SET_IMAGE_TYPE:
      return { ...currentState, imageType };
    case AssetTableActionType.SET_LIMIT:
      return { ...currentState, limit };
    case AssetTableActionType.SET_SORT_BY:
      return { ...currentState, sortBy };
    case AssetTableActionType.SET_SORT_DIRECTION:
      return { ...currentState, sortDirection };
    default:
      return currentState;
  }
}

export { AssetTableActionType, assetTableFilterReducer, transformStateToQueryParams, DEFAULT_FILTER_STATE };
export type { AssetTableState };
